import React from 'react';
import { Sparkles, AlertTriangle, CheckCircle2, Flame } from 'lucide-react';
import Surface from './Surface';
import { cn } from '../../utils/tailwindUtils';

const STATE_STYLES = {
  critical: {
    accent: 'from-rose-500/20 via-rose-500/5 to-transparent',
    ring: 'ring-rose-500/30 dark:ring-rose-500/40',
    icon: 'bg-rose-100 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400',
    Icon: Flame,
    label: 'Critical',
  },
  warning: {
    accent: 'from-amber-500/20 via-amber-500/5 to-transparent',
    ring: 'ring-amber-500/30 dark:ring-amber-500/40',
    icon: 'bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400',
    Icon: AlertTriangle,
    label: 'Needs Attention',
  },
  healthy: {
    accent: 'from-emerald-500/15 via-emerald-500/5 to-transparent',
    ring: 'ring-emerald-500/20 dark:ring-emerald-500/30',
    icon: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400',
    Icon: CheckCircle2,
    label: 'On Track',
  },
  info: {
    accent: 'from-indigo-500/15 via-indigo-500/5 to-transparent',
    ring: 'ring-indigo-500/20 dark:ring-indigo-500/30',
    icon: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400',
    Icon: Sparkles,
    label: 'Today',
  },
};

/**
 * The "founder's digest" — the single most important takeaway at the top.
 * Props:
 *   - state: 'critical' | 'warning' | 'healthy' | 'info'
 *   - title: short headline ("Cashflow is 23% below plan")
 *   - description: one-liner context
 *   - actions: [{ label, onClick, intent }]  — up to 3
 *   - metrics: optional [{ label, value, delta, goodUp }] shown as a small stat row
 */
const HeadlineCard = ({ state = 'info', title, description, actions = [], metrics = [], className }) => {
  const s = STATE_STYLES[state] || STATE_STYLES.info;
  const Icon = s.Icon;
  return (
    <Surface
      padding="lg"
      className={cn('relative overflow-hidden ring-1', s.ring, className)}
    >
      <div className={cn('absolute inset-0 -z-0 bg-gradient-to-br pointer-events-none', s.accent)} />
      <div className="relative z-10">
        <div className="flex items-start gap-4">
          <span className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', s.icon)}>
            <Icon className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {s.label}
            </div>
            <h2 className="mt-1 text-lg sm:text-xl font-bold text-foreground leading-snug">
              {title}
            </h2>
            {description && (
              <p className="mt-1.5 text-sm text-muted-foreground max-w-3xl">{description}</p>
            )}
            {actions.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {actions.map((a, i) => (
                  <button
                    key={i}
                    onClick={a.onClick}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors',
                      a.intent === 'primary'
                        ? 'bg-primary text-primary-foreground hover:opacity-90'
                        : 'bg-card border border-border text-foreground hover:bg-accent'
                    )}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {metrics.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
            {metrics.map((m, i) => (
              <div key={i}>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.label}</div>
                <div className="mt-0.5 text-base font-bold text-foreground">{m.value}</div>
                {m.hint && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Surface>
  );
};

export default HeadlineCard;
